import React, { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react'

import { Grid } from '@mui/material'
import Accordion from '_/views/finance/components/Accordion'
import { NumericInput } from '_views/budget-view-v2/components/Inputs/NumericInput'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import { Attachments } from '_/components/refera-ui'
import {
  RESOLUTION_TABLE_COLUMNS,
  RESOLUTION_TABLE_DEFAULT_ITEM,
} from '_/views/budget-view-simplified/utils/constants'

import { useFormContext, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { useLocation, useParams } from '@reach/router'

import useStyles from './styles'

const NAME = 'budgetResolutions[0].problemMedia'
const IMAGES_FIELD = 'budgetPictures'
const DELETE_IMAGES_FIELD = 'budgetPicturesToDelete'
const TEXT_VALUES = 'Orçamento simplificado'

/** Orçamento simplificado = um único valor (igual ao app). Várias linhas viram uma, somando mão de obra. */
function mergeSimplifiedBudgetRows(rows) {
  if (!rows?.length) {
    return { ...RESOLUTION_TABLE_DEFAULT_ITEM }
  }
  const base = { ...RESOLUTION_TABLE_DEFAULT_ITEM, ...rows[0] }
  const laborSum = rows.reduce((sum, r) => sum + (Number(r?.laborValue) || 0), 0)
  if (laborSum > 0) {
    base.laborValue = laborSum
  }
  const withMedia = rows.find(r => r?.problemMedia?.length)
  base.problemMedia = withMedia?.problemMedia ?? rows[0]?.problemMedia ?? []
  base.problemDescription = rows[0]?.problemDescription ?? TEXT_VALUES
  base.serviceDescription = rows[0]?.serviceDescription ?? TEXT_VALUES
  base.problemText = rows[0]?.problemText ?? TEXT_VALUES
  return base
}

function mapToAttachmentFormat(file) {
  if (file instanceof File) return file
  return { id: file?.id, file: file?.picture || file?.file }
}

function getIsPdf(file) {
  if (file instanceof File) return file.type === 'application/pdf'
  if (typeof file?.isPdf === 'boolean') return file.isPdf
  if (typeof file?.is_pdf === 'boolean') return file.is_pdf
  const path = file?.picture || file?.file
  if (typeof path === 'string' && path.toLowerCase().includes('.pdf')) return true
  return false
}

const RenderTableField = memo(function RenderTableField({ rowIndex, column, showLabel }) {
  const { pathname } = useLocation()
  const isCreating = pathname.includes('/novo')
  const { isEditing } = useBudgetForm()

  const { key, type, defaultValue, required, placeholder, prefix = '', style, readOnly } = column
  const label = showLabel ? column.label : ''
  const rowValues = useWatch({ name: `budgetResolutions[${rowIndex}]` })

  const name = `budgetResolutions[${rowIndex}].${key}`

  return (
    <Grid item sx={style}>
      {type === 'currency' && (
        <NumericInput
          label={label}
          name={name}
          placeholder={placeholder}
          prefix={prefix}
          defaultValue={rowValues?.[key] ?? defaultValue}
          disabled={readOnly || (!isEditing && !isCreating)}
          required={required}
          showErrorsOnlyAfterSubmit
        />
      )}
    </Grid>
  )
})

const Columns = memo(function Columns({ rowIndex, onRemove }) {
  return RESOLUTION_TABLE_COLUMNS.map(column => {
    const { key } = column
    const showLabel = rowIndex === 0
    return (
      <RenderTableField
        key={key}
        rowIndex={rowIndex}
        column={column}
        showLabel={showLabel}
        onRemove={onRemove}
      />
    )
  })
})

const ResolutionsRow = memo(function ResolutionsRow({ index, onRemove }) {
  const styles = useStyles()

  return (
    <Grid flexDirection="column" gap={2}>
      <Grid className={styles.row} container wrap="nowrap">
        <Columns rowIndex={index} onRemove={onRemove} />
      </Grid>
    </Grid>
  )
})

export function ValuesAccordion() {
  const styles = useStyles()
  const { control, setValue, getValues } = useFormContext()
  const { addProblemReadOnly } = useBudgetForm()
  const { budgetId } = useParams()
  const seedBudgetRowRef = useRef(false)

  const { fields, append } = useFieldArray({ control, name: 'budgetResolutions' })

  useEffect(() => {
    seedBudgetRowRef.current = false
  }, [budgetId])

  useEffect(() => {
    if (addProblemReadOnly) {
      return
    }
    if (fields.length > 1) {
      const merged = mergeSimplifiedBudgetRows(getValues('budgetResolutions') || [])
      setValue('budgetResolutions', [merged], { shouldValidate: false, shouldDirty: true })
      return
    }
    if (fields.length === 0 && !seedBudgetRowRef.current) {
      seedBudgetRowRef.current = true
      append(RESOLUTION_TABLE_DEFAULT_ITEM)
      setValue(`budgetResolutions[0].problemDescription`, TEXT_VALUES)
      setValue(`budgetResolutions[0].serviceDescription`, TEXT_VALUES)
      setValue(`budgetResolutions[0].problemText`, TEXT_VALUES)
    }
  }, [addProblemReadOnly, fields.length, append, setValue, getValues])

  return (
    <Accordion id="budget-values" title="Seu orçamento">
      <Grid className={styles.wrapper}>
        <Grid className={styles.inputWrapper}>
          {fields?.map((item, index) => (
            <ResolutionsRow key={item.id} item={item} index={index} />
          ))}
        </Grid>
      </Grid>
    </Accordion>
  )
}

export function AttachmentsAccordion() {
  const styles = useStyles()
  const { pathname } = useLocation()
  const { budgetId } = useParams()
  const { control, watch, setValue } = useFormContext()
  const { isEditing } = useBudgetForm()

  const problemMedia = watch(NAME, [])
  const [attachments, setAttachments] = useState([])
  const isCreating = pathname.includes('/novo')
  const pdfListSeededRef = useRef(false)

  useEffect(() => {
    pdfListSeededRef.current = false
    setAttachments([])
  }, [budgetId])

  useEffect(() => {
    if (pdfListSeededRef.current || !problemMedia?.length) return
    pdfListSeededRef.current = true
    setAttachments(
      problemMedia.map(file =>
        file instanceof File
          ? file
          : {
              id: file?.id,
              file: file?.picture || file?.file,
            }
      )
    )
  }, [problemMedia])

  const pdfs = useMemo(
    () => (attachments?.length > 0 ? attachments?.filter(file => getIsPdf(file)) || [] : []),
    [attachments]
  )

  const handleAttachmentsChange = useCallback(
    files => {
      setAttachments([...files])

      setValue(NAME, [...files], {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleRemoveAttachments = useCallback(
    indexToRemove => {
      const updatedAttachments = pdfs.filter((file, index) => index !== indexToRemove)

      setAttachments(updatedAttachments)
      setValue(NAME, updatedAttachments)
    },
    [setValue, pdfs]
  )

  return (
    <Accordion id="attachments" title="Adicione o orçamento PDF" className={styles.rowAccordion}>
      <Grid className={styles.inputWrapper}>
        <Controller
          name={NAME}
          control={control}
          defaultValue={attachments || []}
          render={() => (
            <Attachments
              label="Arquivo PDF"
              content="files"
              files={pdfs || []}
              onDrop={handleAttachmentsChange}
              onRemoveItem={index => handleRemoveAttachments(index)}
              onItemClick={index =>
                window.open(pdfs?.[index]?.file, '_blank', 'noopener,noreferrer')
              }
              accept={{ 'application/pdf': ['.pdf'] }}
              readOnly={!isEditing && !isCreating}
              required
            />
          )}
        />
      </Grid>
    </Accordion>
  )
}

export function ImagesAccordion() {
  const styles = useStyles()
  const { pathname } = useLocation()
  const { budgetId } = useParams()
  const { control, watch, setValue, getValues } = useFormContext()
  const { isEditing } = useBudgetForm()

  const budgetPictures = watch(IMAGES_FIELD, [])
  const [images, setImages] = useState([])
  const isCreating = pathname.includes('/novo')
  const imagesListSeededRef = useRef(false)

  useEffect(() => {
    imagesListSeededRef.current = false
    setImages([])
  }, [budgetId])

  useEffect(() => {
    if (imagesListSeededRef.current || !budgetPictures?.length) return
    imagesListSeededRef.current = true
    setImages(budgetPictures.filter(file => !getIsPdf(file)).map(mapToAttachmentFormat))
  }, [budgetPictures])

  const handleImagesChange = useCallback(
    newFiles => {
      const combined = [...images, ...newFiles]
      setImages(combined)
      setValue(IMAGES_FIELD, combined, { shouldValidate: true, shouldDirty: true })
    },
    [setValue, images]
  )

  const handleRemoveImage = useCallback(
    indexToRemove => {
      const file = images[indexToRemove]
      if (file?.id) {
        const currentToDelete = getValues(DELETE_IMAGES_FIELD) || []
        setValue(DELETE_IMAGES_FIELD, [...currentToDelete, file.id])
      }
      const updated = images.filter((_, i) => i !== indexToRemove)
      setImages(updated)
      setValue(IMAGES_FIELD, updated)
    },
    [images, setValue, getValues]
  )

  return (
    <Accordion id="budget-images" title="Fotos e vídeos" className={styles.rowAccordion}>
      <Grid className={styles.inputWrapper}>
        <Controller
          name={IMAGES_FIELD}
          control={control}
          defaultValue={[]}
          render={() => (
            <Attachments
              label="Fotos e vídeos"
              files={images}
              onDrop={handleImagesChange}
              onRemoveItem={handleRemoveImage}
              onItemClick={index =>
                images[index]?.file &&
                window.open(images[index].file, '_blank', 'noopener,noreferrer')
              }
              accept={{
                'image/*': ['.jpeg', '.jpg', '.png', '.bmp', '.webp'],
                'video/*': ['.mp4', '.mov', '.avi', '.m4v', '.webm'],
              }}
              readOnly={!isEditing && !isCreating}
            />
          )}
        />
      </Grid>
    </Accordion>
  )
}
